<template>
  <div class="pagination">
    <button :disabled="isFirstBlock" @click="goToFirstBlock">«</button>
    <button :disabled="isFirstBlock" @click="goToPrevBlock">‹</button>

    <button
      v-for="page in pageRange"
      :key="page"
      :class="{ active: page === pageNumber }"
      @click="goToPage(page)"
    >
      {{ page }}
    </button>

    <button :disabled="isLastBlock" @click="goToNextBlock">›</button>
    <button :disabled="isLastBlock" @click="goToLastBlock">»</button>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from "vue";

export default defineComponent({
  name: "BlockPaginator",
  props: {
    pageNumber: {
      type: Number,
      required: true,
    },
    totalPages: {
      type: Number,
      required: true,
    },
    blockSize: {
      type: Number,
      default: 10,
    },
  },
  emits: ["update:page"],
  setup(props, { emit }) {
    const currentBlock = computed(() =>
      Math.floor((props.pageNumber - 1) / props.blockSize)
    );

    const startPage = computed(() => currentBlock.value * props.blockSize + 1);

    const endPage = computed(() =>
      Math.min(startPage.value + props.blockSize - 1, props.totalPages)
    );

    const pageRange = computed(() => {
      const range: number[] = [];
      for (let i = startPage.value; i <= endPage.value; i++) {
        range.push(i);
      }
      return range;
    });

    const isFirstBlock = computed(() => currentBlock.value === 0);
    const isLastBlock = computed(() => endPage.value >= props.totalPages);

    const goToPage = (page: number) => {
      emit("update:page", page);
    };

    const goToFirstBlock = () => goToPage(1);
    const goToLastBlock = () => {
      const lastStart =
        Math.floor((props.totalPages - 1) / props.blockSize) * props.blockSize +
        1;
      goToPage(lastStart);
    };

    const goToPrevBlock = () => {
      const targetPage = startPage.value - props.blockSize;
      if (targetPage >= 1) goToPage(targetPage);
    };

    const goToNextBlock = () => {
      const targetPage = startPage.value + props.blockSize;
      if (targetPage <= props.totalPages) goToPage(targetPage);
    };

    return {
      pageRange,
      goToPage,
      goToFirstBlock,
      goToLastBlock,
      goToPrevBlock,
      goToNextBlock,
      isFirstBlock,
      isLastBlock,
    };
  },
});
</script>

<style scoped>
.pagination {
  display: flex;
  gap: 6px;
  justify-content: center;
  padding: 8px;
}
button {
  padding: 8px 15px;
  border: 1px solid var(--t-border-medium);
  background: var(--t-bg-surface);
  cursor: pointer;
  margin: 0px 2px;
  color: var(--t-text-secondary);
  border-radius: 4px;
  transition: background 0.15s, color 0.15s;
}
button:hover:not(:disabled):not(.active) {
  background: var(--t-bg-hover);
}
button.active {
  border-radius: 5px;
  color: var(--t-accent-primary);
  border-color: var(--t-accent-primary);
  font-weight: bold;
  background: var(--t-accent-bg);
}
button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

@media (max-width: 768px) {
  button {
    padding: 4px 8px;
    border: 1px solid var(--t-border-medium);
    background: var(--t-bg-surface);
    cursor: pointer;
    margin: 0px 0px;
    color: var(--t-text-secondary);
  }
}
</style>
